/* eslint-disable @typescript-eslint/no-use-before-define */
import { createReducer, on, StoreModule } from '@ngrx/store';
import { LocationResponseContract } from '@polaris-customerweb/sharedcomponents.core';
import { CollectionEffectStateBase } from '../../shared/models/collection-effect-state-base';
import { EffectLoadingState } from '../../shared/models/effect-state';
import * as VehicleLocationActions from './vehicle-location.actions';

export const vehicleLocationFeatureKey = 'vehicleLocation';

export interface VehicleLocationState extends CollectionEffectStateBase {
    vehicleLocations: LocationResponseContract[];
    isPollingAllVehicles: boolean;
    isPollingActiveVehicle: boolean;
    hasFetchedAllLocations: boolean;
}

export const initialState: VehicleLocationState = {
    collectionEffectState: {},
    effectState: EffectLoadingState.Init,
    hasFetchedAllLocations: false,
    isPollingActiveVehicle: false,
    isPollingAllVehicles: false,
    vehicleLocations: [],
};

export const vehicleLocationsReducer = createReducer(
    initialState,
    on(VehicleLocationActions.GetVehicleLocation, (state, { vin }) => ({
        ...state,
        collectionEffectState: {
            ...state.collectionEffectState,
            [vin]: EffectLoadingState.Loading,
        },
        effectState: EffectLoadingState.Loading,
    })),
    on(
        VehicleLocationActions.GetVehicleLocationSuccess,
        (state, { locationResponse }) => upsertLocation(state, locationResponse)
    ),
    on(
        VehicleLocationActions.GetVehicleLocationFail,
        (state, { vin, errorMessage }) =>
            setLocationUpdateFailed(state, vin, errorMessage)
    ),
    on(
        VehicleLocationActions.GetAllVehicleLocationsSuccess,
        (state, { locationResponses }) => {
            const locationState = locationResponses.reduce(
                (newState, locationResponse) =>
                    upsertLocation(newState, locationResponse),
                state
            );
            locationState.hasFetchedAllLocations = true;
            return locationState;
        }
    ),
    on(
        VehicleLocationActions.GetAllVehicleLocationsFail,
        (state, { errorMessage, vins }) => {
            const locationState = vins.reduce(
                (newState, vin) =>
                    setLocationUpdateFailed(newState, vin, errorMessage),
                state
            );
            locationState.hasFetchedAllLocations = true;
            return locationState;
        }
    ),
    on(
        VehicleLocationActions.SetIsPollingAllVehicleLocations,
        (state, { isPolling }) => ({
            ...state,
            isPollingAllVehicles: isPolling,
        })
    ),
    on(
        VehicleLocationActions.SetIsPollingActiveVehicleLocation,
        (state, { isPolling }) => ({
            ...state,
            isPollingActiveVehicle: isPolling,
        })
    )
);

export const vehicleLocationStoreModule = StoreModule.forFeature(
    vehicleLocationFeatureKey,
    vehicleLocationsReducer
);

function upsertLocation(
    state: VehicleLocationState,
    locationResponse: LocationResponseContract
): VehicleLocationState {
    const response = state.vehicleLocations.reduce(
        (accumulator, dto) => {
            const { found, vehicleLocations } = accumulator;
            if (locationResponse.vin !== dto.vin) {
                vehicleLocations.push(dto);
                return { found, vehicleLocations };
            }

            vehicleLocations.push(locationResponse);
            return { found: true, vehicleLocations };
        },
        {
            found: false,
            vehicleLocations: [] as LocationResponseContract[],
        }
    );

    if (!response.found) {
        response.vehicleLocations.push(locationResponse);
    }

    const result = {
        ...state,
        collectionEffectState: {
            ...state.collectionEffectState,
            [locationResponse.vin || '']: EffectLoadingState.Loaded,
        },
        vehicleLocations: response.vehicleLocations,
    };

    if (
        Object.values(result.collectionEffectState).every(
            (itemState) => itemState === EffectLoadingState.Loaded
        )
    ) {
        result.effectState = EffectLoadingState.Loaded;
    }

    return result;
}

function setLocationUpdateFailed(
    state: VehicleLocationState,
    vin: string,
    errorMessage: string
): VehicleLocationState {
    return {
        ...state,
        collectionEffectState: {
            ...state.collectionEffectState,
            [vin]: { errorMessage },
        },
        effectState: EffectLoadingState.HasErrors,
    };
}
